import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { AMAZON_URL, NAV_LINKS } from "../lib/site";
import { scrollToSection } from "../lib/scroll";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[#1E2A2B]/8 shadow-[0_8px_30px_rgba(30,42,43,0.06)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-[76px] items-center justify-between">
          <a
            href="#top"
            onClick={(e) => go(e, "#top")}
            data-testid="header-brand"
            className="group flex items-baseline gap-2"
          >
            <span className="font-display text-lg sm:text-xl font-bold tracking-tight text-[#1E2A2B]">
              50 Bookkeeping{" "}
              <span className="italic text-[#14A8A3] group-hover:text-[#20C9C3] transition-colors duration-300">
                Red Flags
              </span>
            </span>
            <span className="hidden sm:block h-1.5 w-1.5 rotate-45 bg-[#C5A253] translate-y-[-2px]" />
          </a>

          <nav className="hidden lg:flex items-center gap-9" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => go(e, link.href)}
                data-testid={`nav-${link.href.slice(1)}`}
                className="relative text-[13px] font-semibold uppercase tracking-[0.14em] text-[#1E2A2B]/70 hover:text-[#1E2A2B] transition-colors duration-300 after:absolute after:left-0 after:-bottom-1.5 after:h-[2px] after:w-0 after:bg-[#20C9C3] after:transition-[width] after:duration-300 hover:after:w-full"
              >
                {link.label}
              </a>
            ))}
            <a
              href={AMAZON_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-buy-button"
              className="group inline-flex items-center gap-1.5 rounded-full bg-[#20C9C3] px-6 py-2.5 text-[13px] font-bold uppercase tracking-[0.12em] text-white shadow-[0_10px_25px_rgba(32,201,195,0.35)] transition-[background-color,transform,box-shadow] duration-300 hover:bg-[#14A8A3] hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgba(32,201,195,0.45)]"
            >
              Buy the Book
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </nav>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#1E2A2B]/10 text-[#1E2A2B]"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-[#1E2A2B]/8"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => go(e, link.href)}
                  data-testid={`mobile-nav-${link.href.slice(1)}`}
                  className="font-display text-2xl text-[#1E2A2B] hover:text-[#14A8A3] transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <a
                href={AMAZON_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-buy-button"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#20C9C3] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.12em] text-white"
              >
                Buy the Book
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
